import * as React from 'react';

import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Input, Row, Select } from 'antd';

import BaseForm from 'components/modules/BaseForm';

import { FIVE_LEVEL_CONTENTS, WEATHER_CONTENTS } from '../../../utils/ResultAnswerUtils';
import { NamePath } from 'rc-field-form/lib/interface';
const Form = BaseForm;
const { Option } = Select;

const FREE_DESCRIPTION_MAX_LENGTH = 3;

type Props = {
    items: EverydayNote.Question[]
    loading: boolean
    name: string | number
    // TODO: 一時的対応
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange?: (value: any) => void
    onRemove: () => void
    value?: { question: string, candidates: string[] }
};

const ConditionFormItem: React.FC<Props> = ({
    items,
    loading,
    name,
    onChange = () => undefined,
    onRemove,
    value = undefined,
}) => {
    const form = Form.useFormInstance<EverydayNote.AnswerResultModal.FormValue>();

    const selectedQuestion = items.find(item => item.uuid === value?.question);
    const type = selectedQuestion?.type;
    const scale = selectedQuestion?.evaluationScaleMax ?? undefined;
    const sliderRangeMin = selectedQuestion?.sliderRangeMin ?? undefined;
    const sliderRangeMax = selectedQuestion?.sliderRangeMax ?? undefined;

    const conditions = Form.useWatch('conditions', form) ?? [];
    const selectedQuestionUuids = conditions.map(condition => condition.question);
    const filteredItems = items.filter(item => {
        return !selectedQuestionUuids.includes(item.uuid) || item.uuid === value?.question;
    });

    return (
        <Row gutter={[16, 12]} >
            <Col span={23}>
                <QuestionSelectFormItem
                    items={filteredItems}
                    loading={loading}
                    name={name}
                    onChange={onChange}
                    scale={scale}
                    sliderRangeMin={sliderRangeMin}
                    sliderRangeMax={sliderRangeMax}
                    type={type}
                    value={value}
                />
            </Col>

            <Col span={1}>
                <Button size='large' onClick={onRemove} type='text' shape="circle" icon={<MinusCircleOutlined />} />
            </Col>
        </Row>
    );
};

export default ConditionFormItem;


type QuestionSelectFormItemProps = {
    name: NamePath
    loading: boolean
    items: EverydayNote.Question[]
    // TODO: 一時的対応
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange?: (value: any) => void
    value?: { question: string, candidates: string[] }
    type: EverydayNote.QuestionTypes | undefined
    scale?: number
    sliderRangeMin?: number
    sliderRangeMax?: number
    hidden?: boolean
}

const QuestionSelectFormItem: React.FC<QuestionSelectFormItemProps> = ({
    type,
    scale = 0,
    sliderRangeMin = 0,
    sliderRangeMax = 0,
    ...restProps
}) => {

    switch (type) {
        case 1:
        case 2:
        case 5:
        case 7:
        case 10: // 段階選択式（単一選択式と同様に選択肢から絞り込む）
            return <ChoiceFormItem {...restProps} />;
        case 3:
            return <FreeDescriptionFormItem {...restProps} />;
        case 4:
            return <FiveLevelFormItem {...restProps} />;
        case 6:
            return <StartFormItem {...restProps} scale={scale} />;
        case 8:
            return <SliderRangeFormItem {...restProps} sliderRangeMin={sliderRangeMin} sliderRangeMax={sliderRangeMax} />;
        case 9:
            return <WeatherFormItem {...restProps} />;
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _: undefined = type;
            return <DefaultFormItem {...restProps} />;
        }
    }
};

const ChoiceFormItem: React.FC<Omit<QuestionSelectFormItemProps, 'type'>> = props => {
    const {
        items,
        value = undefined,
    } = props;
    const candidates = items
        .find(item => item.uuid === value?.question)
        ?.candidates ?? [];

    return (
        <SelectOrConditionsFormItem {...props} candidates={candidates} />
    );
};

const FreeDescriptionFormItem: React.FC<Omit<QuestionSelectFormItemProps, 'type'>> = props => {
    const {
        name,
        loading,
    } = props;
    const candidatesFormItemName = Array.isArray(name) ? name.concat(['candidates']) : [name, 'candidates'];

    return (
        <>
            <Row gutter={[16, 12]}>
                <Col span={12}>
                    <DefaultFormItem
                        {...props}
                    />
                </Col>

                <Col span={11}>
                    <Form.Item name={[...candidatesFormItemName, 0]}>
                        <Input
                            disabled={loading}
                            placeholder='内容を入力してください'
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Form.List name={candidatesFormItemName}>
                {(fields, { add, remove }) => (
                    <>
                        {fields.map((_, index) => {
                            if (index === 0) return null;

                            return (
                                <Row gutter={[16, 12]} key={index}>
                                    <Col offset={12} span={11}>
                                        <Form.Item name={index}>
                                            <Input
                                                disabled={loading}
                                                placeholder='内容を入力してください'
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col span={1}>
                                        <Button
                                            onClick={() => remove(index)}
                                            type='text'
                                            shape='circle'
                                            icon={<MinusCircleOutlined />}
                                        />
                                    </Col>
                                </Row>
                            )
                        })}

                        {fields.length < FREE_DESCRIPTION_MAX_LENGTH && (
                            <Row gutter={[16, 12]}>
                                <Col offset={12} span={12}>
                                    <Form.Item>
                                        <Button type='dashed'
                                            style={{ width: '100%' }}
                                            // add をそのまま渡すと, 以下のエラーが発生する.
                                            // This synthetic event is reused for performance reasons.
                                            onClick={() => add(undefined)}
                                        >
                                            <PlusOutlined />
                                            同じ設問の条件を追加（OR検索）
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </>
                )}
            </Form.List>
        </>
    );
};

const FiveLevelFormItem: React.FC<Omit<QuestionSelectFormItemProps, 'type'>> = props => {
    const candidates = FIVE_LEVEL_CONTENTS.map((level, i) => {
        return {
            content: level,
            order: i,
            uuid: `${5 - i}`,
        };
    });

    return (
        <SelectOrConditionsFormItem {...props} candidates={candidates} />
    );
};

const StartFormItem: React.FC<Omit<QuestionSelectFormItemProps, 'type'> & { scale: number }> = ({
    scale,
    ...props
}) => {
    const candidates = [...Array(scale)].map((_, i) => {
        return {
            content: `${i + 1}`,
            order: i,
            uuid: `${i + 1}`,
        };
    });

    return (
        <SelectOrConditionsFormItem {...props} candidates={candidates} />
    );
};

const SliderRangeFormItem: React.FC<Omit<QuestionSelectFormItemProps, 'type'> & { sliderRangeMin: number, sliderRangeMax: number }> = ({
    sliderRangeMin,
    sliderRangeMax,
    ...props
}) => {
    const candidates = [...Array(sliderRangeMax - sliderRangeMin + 1)].map((_, i) => {
        return {
            content: `${i + sliderRangeMin}`,
            order: i,
            uuid: `${i + sliderRangeMin}`,
        };
    });

    return (
        <SelectOrConditionsFormItem {...props} candidates={candidates} />
    );
};

const SelectOrConditionsFormItem: React.FC<Omit<QuestionSelectFormItemProps, 'type'> & { candidates: EverydayNote.Candidate[] }> = ({
    candidates,
    ...props
}) => {
    const {
        name,
        loading,
        value = undefined,
    } = props;
    const candidatesFormItemName = Array.isArray(name) ? name.concat(['candidates']) : [name, 'candidates'];
    const filteredCandidates = (index: number) => filteredSelectedCandidates(candidates, value, index);

    return (
        <>
            <Row gutter={[16, 12]}>
                <Col span={12}>
                    <DefaultFormItem
                        {...props}
                    />
                </Col>

                <Col span={11}>
                    <Form.Item name={[...candidatesFormItemName, 0]}>
                        <Select
                            disabled={loading}
                            loading={loading}
                            placeholder='選択肢を選択してください'
                        >
                            {filteredCandidates(0).map(candidate => {
                                return (
                                    <Option key={candidate.uuid} value={candidate.uuid}>
                                        {candidate.content}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Form.List name={candidatesFormItemName}>
                {(fields, { add, remove }) => (
                    <>
                        {fields.map((_, index) => {
                            if (index === 0) return null;

                            return (
                                <Row gutter={[16, 12]} key={index}>
                                    <Col offset={12} span={11}>
                                        <Form.Item name={index}>
                                            <Select
                                                disabled={loading}
                                                loading={loading}
                                                placeholder='選択肢を選択してください'
                                            >
                                                {filteredCandidates(index).map(candidate => {
                                                    return (
                                                        <Option key={candidate.uuid} value={candidate.uuid}>
                                                            {candidate.content}
                                                        </Option>
                                                    );
                                                })}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col span={1}>
                                        <Button
                                            onClick={() => remove(index)}
                                            type='text'
                                            shape='circle'
                                            icon={<MinusCircleOutlined />}
                                        />
                                    </Col>
                                </Row>
                            )
                        })}

                        {fields.length < candidates.length && (
                            <Row gutter={[16, 12]}>
                                <Col offset={12} span={12}>
                                    <Form.Item>
                                        <Button type='dashed'
                                            style={{ width: '100%' }}
                                            // add をそのまま渡すと, 以下のエラーが発生する.
                                            // This synthetic event is reused for performance reasons.
                                            onClick={() => add(undefined)}
                                        >
                                            <PlusOutlined />
                                            同じ設問の条件を追加（OR検索）
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </>
                )}
            </Form.List>
        </>
    );
};

const WeatherFormItem: React.FC<Omit<QuestionSelectFormItemProps, 'type'>> = props => {
    const candidates = WEATHER_CONTENTS.map((weather, i) => {
        return {
            content: weather,
            order: i,
            uuid: `${WEATHER_CONTENTS.length - i}`,
        };
    });

    return (
        <SelectOrConditionsFormItem {...props} candidates={candidates} />
    );
};

const DefaultFormItem: React.FC<Omit<QuestionSelectFormItemProps, 'type'>> = ({
    name,
    loading,
    items,
    onChange = () => undefined,
    value = undefined,
    hidden = false,
}) => {
    return (
        <Form.Item
            name={Array.isArray(name) ? name.concat(['question']) : [name, 'question']}
            hidden={hidden}
        >
            <Select
                disabled={loading}
                loading={loading}
                onChange={(value) => onChange({ question: value, candidates: [undefined] })}
                placeholder='設問を選択してください'
                value={value?.question}
            >
                {items.map(item => (
                    <Option key={item.uuid} value={item.uuid}>
                        {item.content}
                    </Option>
                ))}
            </Select>
        </Form.Item>
    );
};

const filteredSelectedCandidates = (candidates: EverydayNote.Candidate[], value: Props['value'], index: number) => {
    const selectedUuidOtherThanItself = value?.candidates.filter((_, i) => i !== index) ?? [];

    return candidates.filter(item => !selectedUuidOtherThanItself.includes(item.uuid));
}
